import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';

import { Loader } from '@asphalt-react/loader';

type AppConfig = {
  [key: string]: string;
};

const AppConfigContext = createContext<AppConfig | undefined>(undefined);

const loadConfig = async () => {
  const isDev = import.meta.env.VITE_ENV_MODE === 'development';

  if (isDev) {
    return {
      VITE_ENV_MODE: import.meta.env.VITE_ENV_MODE,
      VITE_BASE_API_URL: import.meta.env.VITE_BASE_API_URL,
    };
  }

  const response = await fetch('/app-config');

  if (!response.ok) {
    throw new Error(`Failed to fetch app config: ${response.status}`);
  }

  return await response.json();
};

export const AppConfigProvider = ({ children }: { children: ReactNode }) => {
  const [appConfig, setAppConfig] = useState<AppConfig>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchConfig = async () => {
      setIsLoading(true);

      try {
        const config = await loadConfig();
        setAppConfig(config);
      } catch (error) {
        console.error('Error loading app config:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchConfig();
  }, []);

  if (isLoading) {
    return <Loader />;
  }

  return (
    <AppConfigContext.Provider value={appConfig}>
      {children}
    </AppConfigContext.Provider>
  );
};

export const useAppConfig = () => {
  const context = useContext(AppConfigContext);

  if (!context) {
    throw new Error('useAppConfig must be used within AppConfigProvider');
  }

  return context;
};
